<template>
    <div class="IncodeCapture">
        <!-- Loading state: waiting for session + SDK init before mounting the element -->
        <div v-if="state === 'loading'" class="IncodeCapture__loading">
            <div class="IncodeCapture__spinner" :aria-label="`Starting ${flowMeta.label}`" role="status" />
        </div>

        <!-- Capturing state: Incode SDK drives document capture -->
        <div v-else-if="state === 'capturing'" class="IncodeCapture__captureWrapper">
            <incode-flow
                ref="incodeFlow"
                class="IncodeCapture__flow"
            />
        </div>

        <!-- Complete state: SDK capture done, polling BE in background -->
        <SuccessScreen v-else-if="state === 'complete'">
            <h3>{{ completionHeading }}</h3>
            <p>{{ completionMessage }}</p>
        </SuccessScreen>

        <!-- Error state: session error with optional retry -->
        <div v-else-if="state === 'error'" class="IncodeCapture__error">
            <p class="IncodeCapture__errorText">
                {{ errorMessage }}
            </p>
            <button
                v-if="canRetry"
                class="IncodeCapture__retryButton"
                type="button"
                @click="onRetry"
            >
                Try again
            </button>
        </div>
    </div>
</template>

<style lang="scss">
    .IncodeCapture {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;

        &__captureWrapper {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            width: 100%;
        }

        &__flow {
            display: block;
            width: 100%;
            height: 600px;

            // Hide Incode's logo/branding header
            .IncodeRedirectLogoWrapper,
            .IncodePageHeader {
                display: none !important;
            }

            .IncodeTitle,
            .IncodeRedirectTitle,
            .IncodePageTitle {
                font-weight: 500 !important;
            }

            .IncodeRedirectCard {
                justify-content: unset !important;
                gap: 16px !important;
            }

            .IncodePageContainer {
                height: 600px !important;
            }

            .IncodePageContainer.IncodeChooserPage {
                height: 300px !important;

                .IncodePageInner {
                    gap: 16px !important;
                }
            }
        }

        &__loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 16px;
            gap: 16px;
        }

        &__spinner {
            width: 48px;
            height: 48px;
            border: 4px solid var(--eidTrustGreen, #2eb495);
            border-top-color: transparent;
            border-radius: 50%;
            animation: IncodeCapture-spin 0.8s linear infinite;
        }

        @keyframes IncodeCapture-spin {
            to {
                transform: rotate(360deg);
            }
        }

        &__error {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 40px 16px;
            gap: 16px;
        }

        &__errorText {
            font-size: var(--text-body-size, 1rem);
            color: var(--eidDangerRed, #dc2626);
            text-align: center;
        }

        &__retryButton {
            padding: 10px 24px;
            border: none;
            border-radius: 4px;
            background-color: var(--eidTrustGreen, #2eb495);
            color: var(--eidWhite, #fff);
            font-size: var(--text-body-size, 1rem);
            cursor: pointer;

            &:hover {
                opacity: 0.9;
            }
        }
    }
</style>

<script lang="ts">
    import '@incodetech/web/base.css';
    import '@incodetech/web/themes/light.css';
    import '@incodetech/web/flow/styles.css';
    import '@incodetech/web/flow';

    import { defineComponent } from 'vue';

    import { isVideoSelfieAttributeType, isSelfieImageAttributeType } from '@evidentid/ido-lib/curation/videoSelfie';
    import { RelyingPartyRequestStatusType } from '@evidentid/ido-lib/getIdoRequestStatus';
    import Question from '@evidentid/ido-lib/interfaces/Question';
    import { isQuestionComplete } from '@evidentid/ido-lib/questionUtils';

    import SuccessScreen from '@/components/screens/SuccessScreen.vue';
    import { BIOMETRIC_CONSENT_TITLE, BIOMETRIC_CONSENT_INCODE_CONTENT } from '@/config/biometricConsent';
    import {
        getDocumentScanConfigId,
        getIncodeLanguage,
        getLivenessConfigId,
        getUsOnlyDocumentScanConfigId,
        getUsOnlyLivenessConfigId,
        isUsOnlyRequest,
    } from '@/config/incode';
    import { classifyLangTag } from '@/services/getWeglotLang';
    import { startSession, setConsent, initSdk, pollForCompletion } from '@/services/incodeSessionService';
    import { createSessionLanguageStore } from '@/services/weglotLanguageStore';
    import { useIdoStore } from '@/store/store';

    type CaptureState = 'loading' | 'capturing' | 'complete' | 'error';

    const POLL_MAX_ATTEMPTS = 30;
    const POLL_DELAY_MS = 3000;

    const BIOMETRIC_CONSENT_ATTR_TYPE = 'consent.biometric.authorization';

    const ERROR_MESSAGES: Record<string, string> = {
        consent: 'Biometric consent is required to proceed with identity verification.',
        session: 'We were unable to start a document scan session. Please try again.',
        sdk: 'There was a problem initialising the capture flow. Please try again.',
        flow: 'There was an unexpected error during document capture. Please try again.',
        timeout: 'Your document is still being processed. You can close this page and check back later.',
    };

    export default defineComponent({
        name: 'IncodeCapture',

        components: { SuccessScreen },

        props: {
            question: {
                type: Object as () => Question,
                required: true,
            },
        },
        emits: [ 'submit' ],

        data() {
            return {
                state: 'loading' as CaptureState,
                errorMessage: '' as string,
                canRetry: false as boolean,
                interviewId: null as string | null,
                // Preserved across retries so a setConsent failure can retry without
                // starting a new Incode session.
                pendingToken: null as string | null,
                pollCancelled: false as boolean,
            };
        },

        computed: {
            flowMeta(): { configId: string, label: string, isBiometric: boolean } {
                const attrType = this.question.attrType;
                const questions: Question[] = useIdoStore().questions;
                const isUsOnly = isUsOnlyRequest(questions);

                if (isVideoSelfieAttributeType(attrType) || isSelfieImageAttributeType(attrType)) {
                    return {
                        configId: isUsOnly ? getUsOnlyLivenessConfigId() : getLivenessConfigId(),
                        label: 'selfie',
                        isBiometric: true,
                    };
                }
                return {
                    configId: isUsOnly ? getUsOnlyDocumentScanConfigId() : getDocumentScanConfigId(),
                    label: 'document scan',
                    isBiometric: false,
                };
            },

            completionHeading(): string {
                const issuer = useIdoStore().issuer;
                return issuer?.idoConfirmationMessage?.headingText
                    || `Thank you for submitting your information to ${issuer?.displayName || 'us'}`;
            },

            completionMessage(): string {
                return useIdoStore().issuer?.idoConfirmationMessage?.messageText
                    || 'You will be notified once your submission has finished processing.';
            },
        },

        async mounted() {
            // Re-entry guard: if the question is already complete, emit immediately.
            if (isQuestionComplete(this.question)) {
                this.emitComplete();
                return;
            }

            await this.beginSession();
        },

        beforeUnmount() {
            this.pollCancelled = true;
        },

        methods: {
            async beginSession(): Promise<void> {
                const { isBiometric } = this.flowMeta;

                if (isBiometric) {
                    const questions: Question[] = useIdoStore().questions;
                    const consentQuestion = questions.find((q) => q.attrType === BIOMETRIC_CONSENT_ATTR_TYPE);
                    if (!consentQuestion || !isQuestionComplete(consentQuestion)) {
                        this.enterError('consent', false);
                        return;
                    }
                }

                let token: string;

                // Reuse an existing token when retrying after a setConsent failure —
                // no need to start a new Incode session in that case.
                if (this.pendingToken) {
                    token = this.pendingToken;
                } else {
                    try {
                        const result = await startSession(this.flowMeta.configId);
                        token = result.token;
                        this.interviewId = result.interviewId;
                        this.pendingToken = token;
                    } catch (err) {
                        this.enterError('session', true);
                        return;
                    }
                }

                if (isBiometric) {
                    try {
                        await setConsent(token, {
                            title: BIOMETRIC_CONSENT_TITLE,
                            content: BIOMETRIC_CONSENT_INCODE_CONTENT,
                            status: true,
                        });
                        // Clear only after consent succeeds — a setConsent failure leaves
                        // pendingToken set so the retry can reuse the same session token.
                        this.pendingToken = null;
                    } catch (err) {
                        this.enterError('session', true);
                        return;
                    }
                } else {
                    // No consent step for document-only captures, so nothing to retry with
                    // the same token — clear immediately after the session starts.
                    this.pendingToken = null;
                }

                try {
                    await initSdk(token);
                } catch (err) {
                    this.enterError('sdk', true);
                    return;
                }

                // Mount incode-flow with token and callbacks via direct DOM properties
                this.state = 'capturing';
                await this.$nextTick();
                this.mountIncodeFlow(token);
            },

            mountIncodeFlow(token: string): void {
                const el = this.$refs.incodeFlow as Element | undefined;
                if (!el) {
                    return;
                }

                // Incode ships its own translations and renders its own subtree, so it is
                // told the language rather than left to Weglot. Omitted entirely for an
                // English session, which keeps the config identical to before.
                const remembered = classifyLangTag(createSessionLanguageStore().read());
                const lang = remembered.kind === 'language'
                    ? { lang: getIncodeLanguage(remembered.tag) }
                    : {};

                // Set config and callbacks directly as DOM properties (not Vue attributes)
                (el as any).config = { token, ...lang };
                (el as any).onFinish = this.handleFinish.bind(this);
                (el as any).onError = this.handleError.bind(this);
            },

            async handleFinish(_result: unknown): Promise<void> {
                this.state = 'complete';

                const outcome = await pollForCompletion({
                    maxAttempts: POLL_MAX_ATTEMPTS,
                    delayMs: POLL_DELAY_MS,
                    isCancelled: () => this.pollCancelled,
                    check: async () => {
                        await useIdoStore().reloadInterview({ blockImmediateFinish: false });
                        const questions: Question[] = useIdoStore().questions;
                        const current = questions.find((q) => q.attrType === this.question.attrType);
                        return current ? isQuestionComplete(current) : false;
                    },
                });

                if (outcome === 'cancelled') {
                    return;
                }

                const questions: Question[] = useIdoStore().questions;
                const current = questions.find((q) => q.attrType === this.question.attrType);

                if (current && isQuestionComplete(current)) {
                    this.emitComplete();
                    return;
                }

                if (useIdoStore().requestStatus?.type === RelyingPartyRequestStatusType.processing) {
                    // RPR in_process but webhook not yet confirmed — stay on complete screen.
                    // withInterviewUpdates background polling will redirect once constraintsMet=true.
                    return;
                }

                this.enterError('timeout', false);
            },

            handleError(_err?: unknown, _code?: unknown): void {
                this.enterError('flow', true);
            },

            enterError(type: string, canRetry: boolean): void {
                this.state = 'error';
                this.errorMessage = type === 'session'
                    ? `We were unable to start the ${this.flowMeta.label} session. Please try again.`
                    : ERROR_MESSAGES[type] || ERROR_MESSAGES.flow;
                this.canRetry = canRetry;
            },

            async onRetry(): Promise<void> {
                this.pollCancelled = false;
                this.interviewId = null;
                this.errorMessage = '';
                this.canRetry = false;
                this.state = 'loading';
                await this.beginSession();
            },

            emitComplete(): void {
                this.$emit('submit');
            },
        },
    });
</script>
